import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Award, Building2, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { optimizedImageUrl } from '@/lib/imageOptimizer';

const PAGE_SIZE = 8;
const AUTO_MS = 5000;

export function TitaniumAgencies() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);

  const { data: agencies } = useQuery({
    queryKey: ['titanium-agencies'],
    queryFn: async () => {
      const { data: ags } = await supabase
        .from('agencies')
        .select('id, name, logo_url, city')
        .eq('is_verified', true)
        .order('created_at', { ascending: false })
        .limit(40);
      const list = ags || [];
      if (!list.length) return [];

      // Compute listing counts per agency via agent_profiles → properties
      const { data: members } = await supabase
        .from('agent_profiles')
        .select('agency_id, user_id')
        .in('agency_id', list.map(a => a.id));
      const userIds = (members || []).map(m => m.user_id);

      const counts: Record<string, number> = {};
      if (userIds.length) {
        const { data: props } = await supabase
          .from('properties')
          .select('user_id')
          .in('user_id', userIds)
          .eq('status', 'active')
          .eq('approval_status', 'approved');
        const perUser: Record<string, number> = {};
        (props || []).forEach((p: any) => { perUser[p.user_id] = (perUser[p.user_id] || 0) + 1; });
        (members || []).forEach((m: any) => {
          counts[m.agency_id] = (counts[m.agency_id] || 0) + (perUser[m.user_id] || 0);
        });
      }

      return list
        .map(a => ({ ...a, properties_count: counts[a.id] || 0 }))
        .sort((a, b) => (b.properties_count - a.properties_count) || a.name.localeCompare(b.name));
    },
  });

  const pages = useMemo(() => {
    if (!agencies?.length) return [];
    const out: any[][] = [];
    for (let i = 0; i < agencies.length; i += PAGE_SIZE) out.push(agencies.slice(i, i + PAGE_SIZE));
    return out;
  }, [agencies]);

  useEffect(() => {
    if (paused || pages.length <= 1) return;
    const t = setInterval(() => setPage(p => (p + 1) % pages.length), AUTO_MS);
    return () => clearInterval(t);
  }, [paused, pages.length]);

  if (!agencies?.length) return null;

  const prev = () => setPage(p => (p - 1 + pages.length) % pages.length);
  const next = () => setPage(p => (p + 1) % pages.length);

  return (
    <section className="bg-muted/40 py-10">
      <div className="container">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            <h2 className="font-display text-xl md:text-2xl font-bold">Titanium Agencies</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={prev} aria-label="Previous agencies" className="h-8 w-8 rounded-full border bg-card flex items-center justify-center hover:bg-muted">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={next} aria-label="Next agencies" className="h-8 w-8 rounded-full border bg-card flex items-center justify-center hover:bg-muted">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          className="overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${page * 100}%)` }}
          >
            {pages.map((group, gi) => (
              <div key={gi} className="w-full shrink-0 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 grid-rows-2 gap-4">
                {group.map((a: any) => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/agencies/${a.id}`)}
                    className="flex items-center gap-3 p-3 rounded-lg bg-card border hover:shadow-md transition text-left"
                  >
                    <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                      {a.logo_url ? (
                        <img src={optimizedImageUrl(a.logo_url, { width: 112, height: 112 })} alt={a.name} className="h-full w-full object-cover" loading="lazy" />
                      ) : (
                        <Building2 className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{a.name}</div>
                      <div className="flex items-center gap-1 text-xs text-primary mt-0.5">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {a.properties_count > 0
                          ? `${a.properties_count} listing${a.properties_count > 1 ? 's' : ''} · ${a.city || 'Ethiopia'}`
                          : (a.city || 'Ethiopia')}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {pages.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-4">
            {pages.map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                aria-label={`Page ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === page ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
